import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import 'react-datepicker/dist/react-datepicker.css';
import { getDBName } from '@demandproai/shared';
import { CHART_COLORS, CHART_FONT_SIZES } from "./chartTheme";

const HorizontalGraph = ({ dataPoints, handleOpenFirmDashboard }) => {

    const chartRef = useRef();

    const drawChart = useCallback(() => {

        if (!dataPoints || dataPoints.length === 0) return;
        const width = 400;
        const height = dataPoints.length * 50;
        const margin = { top: 100, right: 50, bottom: 30, left: 100 };
        const labelToDomainMap = Object.fromEntries(dataPoints.map(d => [d.label, d.domainName]));
        d3.select(chartRef.current).selectAll("*").remove(); // Clear previous SVG content

        const svg = d3.select(chartRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left}, ${margin.top})`);

        const xScale = d3.scaleLinear()
            .domain([0, d3.max(dataPoints, d => d.y) || 1])
            .range([0, width]);
        const yScale = d3.scaleBand()
            .domain(dataPoints.map(d => d.label))
            .range([0, height])
            .padding(0.6); // Increased padding for more gap

        const yAxis = svg.append("g")
            .call(d3.axisLeft(yScale).tickSize(0).tickPadding(5));

        yAxis.select(".domain").remove();

        yAxis.selectAll("text")
            .attr("fill", CHART_COLORS.label)
            .style("cursor", "pointer")
            .on("click", (event, d) => {
                if (handleOpenFirmDashboard) {
                    const domainName = labelToDomainMap[d];
                    if (domainName) {
                        const modifiedDomainName = getDBName(domainName);
                        handleOpenFirmDashboard(modifiedDomainName);
                    } else {
                        console.warn("No domainName found for label:", d);
                    }
                }
            });

        // Draw bars with only the right side rounded
        svg.selectAll(".bar")
            .data(dataPoints)
            .enter()
            .append("path")
            .attr("d", d => {
                const barWidth = xScale(d.y);
                const barHeight = yScale.bandwidth() * 0.7; // Reduced height
                const radius = 5; // Rounded corner radius

                return `
                        M 0,0 
                        H ${barWidth - radius} 
                        A ${radius},${radius} 0 0 1 ${barWidth},${radius} 
                        V ${barHeight - radius} 
                        A ${radius},${radius} 0 0 1 ${barWidth - radius},${barHeight} 
                        H 0 
                        Z
                    `;
            })
            .attr("transform", d => `translate(0,${yScale(d.label)})`)
            .attr("fill", CHART_COLORS.bar);

        // Add labels on bars
        svg.selectAll(".text")
            .data(dataPoints)
            .enter()
            .append("text")
            .attr("x", d => xScale(d.y) + 5)
            .attr("y", d => yScale(d.label) + yScale.bandwidth() / 1.5) // Adjusted label position
            .attr("fill", CHART_COLORS.label)
            .attr("font-size", CHART_FONT_SIZES.label)
            .attr("font-weight", "normal")
            .text(d => d.y);

        // Add X Axis Labels (remove axis lines)
        const xAxis = svg.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xScale).ticks(7).tickSize(7)); // No tick lines
        xAxis.select(".domain").remove(); // Remove the X-axis line
        xAxis.selectAll("text").attr("fill", CHART_COLORS.label);
        xAxis.selectAll("line").attr("stroke", CHART_COLORS.axis);

    }, [dataPoints, handleOpenFirmDashboard]);

    useEffect(() => {
        drawChart();
    }, [drawChart]);

    return (
        <>
            <div className="py-5 text-center">
                <svg ref={chartRef}></svg>
            </div>
        </>
    );
};

export default HorizontalGraph;